/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.

For any inquiry, please contact Colibryx S.r.l. at info@colibryx.com.
*/

import { CommonModule } from "@angular/common";
import {
	ChangeDetectionStrategy,
	Component,
	inject,
	input,
	output,
} from "@angular/core";
import { RGB_COLORS } from "@app/constants/colors.const";
import { TranslateService } from "@ngx-translate/core";
import { DotComponent } from "../dot/dot.component";
import { IconComponent } from "../icon/icon.component";

@Component({
	selector: "cb-chip",
	imports: [CommonModule, IconComponent, DotComponent],
	templateUrl: "./chip.component.html",
	styleUrl: "./chip.component.scss",
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChipComponent {
	private readonly colors: string[] = RGB_COLORS;
	private readonly translate: TranslateService = inject(TranslateService);

	public readonly size = input<"xs" | "sm" | "md" | "lg" | "xl">("md");
	public readonly iconPosition = input<"left" | "right">("right");
	public readonly rounded = input<"pill" | "badge">("pill");
	public readonly type = input<"color" | "outline" | "modern" | "minimal">(
		"color",
	);
	public readonly iconOnly = input(false);
	public readonly iconType = input<
		"none" | "dot" | "avatar" | "x-close" | "icon"
	>("dot");
	public readonly icon = input<string>();
	public readonly clickable = input<boolean>(false);

	public readonly color = input<
		| "mapped"
		| "auto"
		| "brand"
		| "gray"
		| "error"
		| "warning"
		| "success"
		| "info"
		| "custom"
		| "pink"
		| "dark-orange"
		| "green"
		| "rose"
	>("brand");

	public readonly customColor = input<string>();

	public readonly colorMap =
		input<
			Record<string, Exclude<ReturnType<typeof this.color>, "mapped" | "auto">>
		>();
	public readonly labelMap = input<Record<string, string>>();
	public readonly value = input<string>();

	public readonly label = input<string>();

	public readonly translateLabels = input<boolean>();

	public readonly closeChip = output<void>();

	public get currentLabel(): string {
		let label = this.label() ?? "";

		const labelMap = this.labelMap();
		const value = this.value();
		if (labelMap && value) {
			label = labelMap[value] ?? label;
		}

		return this.translateLabels() && label.length > 0
			? this.translate.instant(label)
			: label;
	}

	public get currentCustomColor(): string | undefined {
		const colorValue = this.color();
		const customColor = this.customColor();
		if (!(colorValue === "custom" && customColor) && colorValue !== "auto")
			return undefined;

		const color =
			colorValue === "custom" && customColor ? customColor : this.autoColor;

		// Check if the color is in RGB format
		if (color.startsWith("rgb")) {
			// Convert color to RGB format
			const rgb = color
				.replace("rgb(", "")
				.replace(")", "")
				.split(",")
				.map((color) => Number.parseInt(color.trim()));

			if (rgb) {
				return `${rgb[0]}, ${rgb[1]}, ${rgb[2]}`;
			}
		} else if (color.startsWith("#")) {
			// Convert color from HEX to RGB
			const hex = color.substring(1);
			const rgb = [
				Number.parseInt(hex.slice(0, 2), 16),
				Number.parseInt(hex.slice(2, 4), 16),
				Number.parseInt(hex.slice(4, 6), 16),
			];

			return `${rgb[0]}, ${rgb[1]}, ${rgb[2]}`;
		}

		return color;
	}

	public get autoColor(): string {
		const color = this.color();
		if (color === "auto") {
			const mapItem = this.label() ?? this.value() ?? this.currentLabel;

			if (mapItem) {
				// Get a random color from the colors array calculated based on the label
				let hash = 0;
				for (let i = 0; i < mapItem.length; i++) {
					hash = mapItem.charCodeAt(i) + ((hash << 5) - hash);
				}

				const index = Math.abs(hash) % this.colors.length;
				return this.colors[index];
			}

			// Return a random color from the colors array
			return this.colors[Math.floor(Math.random() * this.colors.length)];
		}

		return color;
	}

	public get dotColor(): Exclude<ReturnType<typeof this.color>, "mapped"> {
		const color = this.color();
		if (color === "mapped") {
			const value = this.value();
			const colorMap = this.colorMap();
			if (value && colorMap?.[value]) {
				return colorMap[value];
			}

			return "gray";
		}

		return color;
	}

	public get currentColor(): ReturnType<typeof this.color> {
		const color = this.color();
		const value = this.value();
		const colorMap = this.colorMap();
		if (color === "mapped" && value && colorMap?.[value]) {
			return colorMap[value];
		}

		return color;
	}

	public closeChipClick(event: MouseEvent): void {
		event.stopImmediatePropagation();
		this.closeChip.emit();
	}
}
